import { useEffect,useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchTenantUserPaginatedMemberships } from 'common/services/api/TenantUserMembership/TenantUserMembership.thunk';
import { License } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import {
    _getNumberOfUsersToFetch,
    fetchAllLicensesUsers,
    getLimitedNumberOfUniqueLicensesUsersSortedByEmail,
    getUniqueLicensesUsersSortedByEmailWithoutLimitNumber,
    isPromise,
} from '../helpers';
import {
    LicensesUsersPromiseList,
    LicensesUsersResponse,
    User } from '../typeDefs';

const USERS_DEFAULT_TAKE = 1000;
const USERS_TYPE_NAME = 'License Users';

export const useFetchLicensesUsers = (licenses: License[], userLimit: string, userLimitDisabled: boolean): {
    processing: boolean,
    users: User[]
} => {
    const [users, setUsers] = useState<User[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const numberOfUsersToFetch = _getNumberOfUsersToFetch(userLimit, userLimitDisabled, USERS_DEFAULT_TAKE);

    const _populateComputedUsersResultToState = (
        fetchedLicensesUsersList: LicensesUsersResponse[],
        userLimitProp?: number
    ) => {
        const uniqueSortedUsers = userLimitProp ?
            getLimitedNumberOfUniqueLicensesUsersSortedByEmail(fetchedLicensesUsersList, userLimitProp) :
            getUniqueLicensesUsersSortedByEmailWithoutLimitNumber(fetchedLicensesUsersList);
        setUsers(uniqueSortedUsers);
        setProcessing(false);
    };

    const _handleComputeUsersFromPromise = (
        allLicensesUsersResponse: Promise<LicensesUsersResponse[]>,
        userGroupsUsersFetchedOnFirstPageLoad: LicensesUsersResponse[],
        userLimitProp?: number
    ) => {
        allLicensesUsersResponse.then(theRestOfFetchedUsers => {
            const allFetchedUsersList = [
                ...userGroupsUsersFetchedOnFirstPageLoad,
                ...theRestOfFetchedUsers
            ];
            _populateComputedUsersResultToState(allFetchedUsersList, userLimitProp);
        })
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), USERS_TYPE_NAME, enqueueSnackbar);
                setProcessing(false);
            });
    };

    const _handleComputeFetchedUsers = (
        licensesUsersFetchedOnFirstPageLoad: LicensesUsersResponse[],
        userLimitProp?: number
    ) => {
        const fetchedUsersListPromise = fetchAllLicensesUsers(
            licensesUsersFetchedOnFirstPageLoad,
            USERS_DEFAULT_TAKE,
            userLimitProp
        );
        if (fetchedUsersListPromise && isPromise(fetchedUsersListPromise)) {
            _handleComputeUsersFromPromise(
                fetchedUsersListPromise,
                licensesUsersFetchedOnFirstPageLoad,
                userLimitProp
            );
        } else {
            _populateComputedUsersResultToState(licensesUsersFetchedOnFirstPageLoad, userLimitProp);
        }
    };

    const _handleComputeLimitedNumberOfUsers = (
        userGroupsUsersFetchedOnFirstPageLoad: LicensesUsersResponse[],
        userLimitProp: number
    ) => {
        if (userLimitProp > USERS_DEFAULT_TAKE) {
            _handleComputeFetchedUsers(userGroupsUsersFetchedOnFirstPageLoad, userLimitProp);
        } else {
            _populateComputedUsersResultToState(userGroupsUsersFetchedOnFirstPageLoad, userLimitProp);
        }
    };

    useEffect(() => {
        setProcessing(true);
        const licenseUsersPromise = licenses.map((license) => {
            const requestParams = {
                tenantId: license.tenantId,
                licenseId: license.id,
                skip: 0,
                take: numberOfUsersToFetch
            };
            return dispatch(fetchTenantUserPaginatedMemberships(requestParams))
                .unwrap()
                .catch((error) => {
                    handleListFetchError(extractStatusCode(error.message), USERS_TYPE_NAME, enqueueSnackbar);
                }) as LicensesUsersPromiseList;
        });

        Promise
            .all(licenseUsersPromise)
            .then((userGroupUsers) => {
                const filteredUserGroupsUsers = userGroupUsers.filter(item => !!item);

                if (userLimitDisabled) {
                    _handleComputeFetchedUsers(filteredUserGroupsUsers);
                } else {
                    _handleComputeLimitedNumberOfUsers(filteredUserGroupsUsers, Number(userLimit));
                }
            }).catch((error) => {
                handleListFetchError(extractStatusCode(error.message), USERS_TYPE_NAME, enqueueSnackbar);
                setProcessing(false);
            });
    }, [licenses.length, userLimitDisabled]);

    return {
        processing,
        users
    };
};
